import { APP_CONFIG } from '@/constants/app';
import { tokenManager } from '@/utils/token-manager';
import axios from 'axios';
import {
  BrevoSender,
  CreateConfigurationDto,
  CreateSenderDto,
  Sender,
  SenderConfiguration,
  SyncResponse,
  UpdateConfigurationDto,
  UpdateSenderDto,
  ValidateSenderDto,
} from '../types/sender.types';

// Create axios instance for senders service
const sendersApi = axios.create({
  baseURL: APP_CONFIG.api.baseUrl + '/senders',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Setup automatic token refresh interceptors
tokenManager.setupInterceptors(sendersApi);

export const sendersService = {
  // Senders CRUD
  getAll: async (): Promise<Sender[]> => {
    const response = await sendersApi.get<{ data: Sender[] }>('/');
    return response.data.data;
  },

  getById: async (uuid: string): Promise<Sender> => {
    const response = await sendersApi.get<Sender>(`/${uuid}`);
    return response.data;
  },

  create: async (sender: CreateSenderDto): Promise<Sender> => {
    const response = await sendersApi.post<Sender>('/', sender);
    return response.data;
  },

  update: async (uuid: string, sender: UpdateSenderDto): Promise<Sender> => {
    const response = await sendersApi.put<Sender>(`/${uuid}`, sender);
    return response.data;
  },

  delete: async (uuid: string): Promise<void> => {
    await sendersApi.delete(`/${uuid}`);
  },

  validate: async (uuid: string, otp: ValidateSenderDto): Promise<Sender> => {
    const response = await sendersApi.post<Sender>(`/${uuid}/validate`, otp);
    return response.data;
  },

  setDefault: async (uuid: string): Promise<Sender> => {
    const response = await sendersApi.post<Sender>(`/${uuid}/default`);
    return response.data;
  },

  getCurrentDefault: async (): Promise<Sender | null> => {
    const response = await sendersApi.get<Sender>('/default/current');
    return response.data;
  },

  // Configurations
  getConfigurations: async (): Promise<SenderConfiguration[]> => {
    // L'API enveloppe ses listes dans { data, meta } (comme `getAll`). Cette
    // route rendait 500 jusqu'au 20/09/2026 (ordre des routes côté API), donc
    // personne n'avait vu que l'enveloppe n'était pas dépliée.
    const response = await sendersApi.get<{ data: SenderConfiguration[] }>(
      '/configurations'
    );
    return response.data.data;
  },

  createConfiguration: async (
    config: CreateConfigurationDto
  ): Promise<SenderConfiguration> => {
    const response = await sendersApi.post<{ data: SenderConfiguration }>(
      '/configurations',
      config
    );
    return response.data.data;
  },

  updateConfiguration: async (
    uuid: string,
    config: UpdateConfigurationDto
  ): Promise<SenderConfiguration> => {
    const response = await sendersApi.put<SenderConfiguration>(
      `/configurations/${uuid}`,
      config
    );
    return response.data;
  },

  deleteConfiguration: async (uuid: string): Promise<void> => {
    await sendersApi.delete(`/configurations/${uuid}`);
  },

  // OAuth
  getOAuthAuthorizeUrl: async (): Promise<{ url: string }> => {
    const response = await sendersApi.get<{ data: { url: string } }>(
      '/oauth/microsoft/authorize',
    );
    return response.data.data;
  },

  getGoogleOAuthAuthorizeUrl: async (): Promise<{ url: string }> => {
    const response = await sendersApi.get<{ data: { url: string } }>(
      '/oauth/google/authorize',
    );
    return response.data.data;
  },

  disconnectOAuth: async (uuid: string): Promise<{ success: boolean }> => {
    const response = await sendersApi.post<{ success: boolean }>(
      `/oauth/microsoft/disconnect/${uuid}`,
    );
    return response.data;
  },

  // Brevo Integration
  syncWithBrevo: async (): Promise<SyncResponse> => {
    const response = await sendersApi.post<SyncResponse>('/sync');
    return response.data;
  },

  getBrevoSenders: async (): Promise<{ senders: BrevoSender[] }> => {
    const response = await sendersApi.get<{ senders: BrevoSender[] }>(
      '/brevo/available'
    );
    return response.data;
  },
};
